import { Button, Switch, errorTextClassName } from '@shared/ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect, useCallback } from 'react';
import { useUserStore } from '../../../auth/store/userStore';
import { useUIStore } from '../../../../shared/store/uiStore';
import { updateProfile } from '../../services/profileService';
import { OrganizationSection, PersonalInfoSection, BillingAddressSection, ShippingAddressSection, MainContactSection, TransferOwnershipSection, MfaSection, AuthenticatorSection } from '../index';
import { hasAccountOwnerRole, hasBillingAdminRole } from '../../../../shared/constants/roles';
import { useMainContact } from '../../hooks/useMainContact';
import { useUsernameAvailability } from '../../hooks/useUsernameAvailability';
import { createRoleBasedSchema } from './profileSchema';
import { saveRow, saveButton } from '../profileStyles';
import { showToast } from '../../../../shared/services/toast';
import { regionLine4Value } from '../../../../shared/hooks/useCountryRegionOptions';


// The form fields owned by the My Profile tab (the signed-in user's own details). Every other
// field — company name, billing, shipping, main contact — is a company setting and belongs to
// Organization Profile.
const PERSONAL_FIELDS = new Set(['firstName', 'lastName', 'username', 'email', 'phone']);

function buildProfilePayload({ data, canEditBillingAndContact, getCountryById, getRegionById, siteId, clientId }) {
  // Free text on HaloPSA's side (Address Line 4: the region's short code), resolved from the same
  // `regions` store list useBillingAddress already populated.
  const shippingState = data.shippingRegion
    ? regionLine4Value(useUserStore.getState().regions.find((r) => r.id.toString() === data.shippingRegion))
    : '';

  const mainContact = canEditBillingAndContact
    ? {
        id: data.mainContactId ? Number.parseInt(data.mainContactId) : null,
        username: data.mainContactUsername || '',
        isCurrentMainContact: false,
      }
    : {
        name: data.contactName,
        email: data.contactEmail,
        phone: data.contactPhone,
      };

  return {
    firm: { firmName: data.firmName },
    personalInfo: {
      firstName: data.firstName,
      lastName: data.lastName,
      username: data.username,
      email: data.email,
      phone: data.phone,
    },
    billingAddress: {
      address1: data.address1,
      address2: data.address2,
      address3: data.address3,
      // Send both IDs and names for backward compatibility
      countryId: data.country ? Number.parseInt(data.country) : null,
      country: getCountryById(data.country)?.name || '',
      regionId: data.region ? Number.parseInt(data.region) : null,
      // Address Line 4 stores the region's short code (its name when HaloPSA has none).
      region: data.region ? regionLine4Value(getRegionById(data.region)) : '',
      postalCode: data.postalCode,
      billingEmail: data.billingEmail,
    },
    shippingAddress: {
      address1: data.shippingAddress1,
      address2: data.shippingAddress2,
      address3: data.shippingAddress3,
      state: shippingState,
      postalCode: data.shippingPostalCode,
      // Drives HaloPSA's own "Invoice address is different from the Site Address" checkbox (inverted).
      sameAsBilling: data.shippingSameAsBilling === true,
    },
    mainContact,
    siteId,
    clientId,
  };
}

function buildProfileResetData(resultData, mainContactSelectedId, shippingRegion, mainContactUsername) {
  return {
    firmName: resultData?.firm?.firmName || '',
    firstName: resultData?.personalInfo?.firstName || '',
    lastName: resultData?.personalInfo?.lastName || '',
    username: resultData?.personalInfo?.username || '',
    email: resultData?.personalInfo?.email || '',
    phone: resultData?.personalInfo?.phone || '',
    address1: resultData?.billingAddress?.address1 || '',
    address2: resultData?.billingAddress?.address2 || '',
    address3: resultData?.billingAddress?.address3 || '',
    region: resultData?.billingAddress?.regionId?.toString() || resultData?.billingAddress?.region || '',
    postalCode: resultData?.billingAddress?.postalCode || '',
    country: resultData?.billingAddress?.countryId?.toString() || resultData?.billingAddress?.country || '',
    billingEmail: resultData?.billingAddress?.billingEmail || '',
    shippingAddress1: resultData?.shippingAddress?.address1 || '',
    shippingAddress2: resultData?.shippingAddress?.address2 || '',
    shippingAddress3: resultData?.shippingAddress?.address3 || '',
    // Left for the useShippingAddress effect to re-resolve by label once regions reload.
    shippingRegion: shippingRegion || '',
    shippingPostalCode: resultData?.shippingAddress?.postalCode || '',
    shippingSameAsBilling: resultData?.shippingAddress?.sameAsBilling === true,
    contactName: resultData?.mainContact?.name || '',
    contactEmail: resultData?.mainContact?.email || '',
    contactPhone: resultData?.mainContact?.phone || '',
    // Keep the contact that was just saved.
    mainContactId: mainContactSelectedId || '',
    mainContactUsername: mainContactUsername || '',
  };
}

/**
 * One form behind two tabs: `view` picks which half is on screen ('organization' for the
 * Organization Profile tab — company settings: company name, "Require MFA for all users", billing,
 * shipping, main contact, transfer ownership, delete account; 'personal' for My Profile — the
 * user's Personal Information and their own authenticator). The other half is hidden with CSS, never unmounted — Billing,
 * Shipping and Main Contact fill in country, region and main contact from effects that only run
 * while mounted, so a save made from My Profile before they had mounted would fail validation (or
 * send blanks). It also keeps unsaved edits when switching between the tabs. ProfileModal renders
 * this at the same position for both tabs, so React keeps the one instance.
 */
export function ProfileInfo({ onLoadingChange, view = 'organization', onViewChange, dataTestId }) {
  const { 
    profile, 
    updateProfile: updateStoreProfile, 
    getCountryById,
    getRegionById
  } = useUserStore();
  const { isDarkMode, toggleTheme } = useUIStore();
  const [isLoading, setIsLoading] = useState(false);
   
// Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading, 'Updating profile...');
    }
  }, [isLoading, onLoadingChange]);
  
  // Role-based visibility and editability
  const userRoles = profile?.roles || [];
  const hasAccountOwner = hasAccountOwnerRole(userRoles);
  const hasBillingAdmin = hasBillingAdminRole(userRoles);
  
  const canEditCompany = hasAccountOwner;
  const canViewBillingAndContact = hasAccountOwner || hasBillingAdmin;
  const canEditBillingAndContact = hasAccountOwner || hasBillingAdmin;
  
  // The main contact can only be required once there is something to pick, but the options are
  // loaded by a hook that needs this form's setValue — so the answer is not known when the form is
  // built. react-hook-form passes its `context` to the resolver at validation time, which is exactly
  // the channel for a value that is only settled later.
  // Stable object so react-hook-form keeps the same reference; its contents are refreshed below once
  // the options have loaded, and the resolver reads them on the next validation.
  const [formContext] = useState(() => ({ hasContactOptions: false }));

  const resolver = useCallback(
    (values, context, options) =>
      zodResolver(
        createRoleBasedSchema(canViewBillingAndContact, context?.hasContactOptions === true, canEditCompany)
      )(values, context, options),
    [canViewBillingAndContact, canEditCompany]
  );

  const {register,handleSubmit,formState: { errors, isSubmitting, isSubmitted },
    reset,watch,setValue,getValues} = useForm({
    resolver,
    context: formContext,
    defaultValues: {
      firmName: profile?.firm?.firmName || '',
      firstName: profile?.personalInfo?.firstName || '',
      lastName: profile?.personalInfo?.lastName || '',
      username: profile?.personalInfo?.username || '',
      email: profile?.personalInfo?.email || '',
      phone: profile?.personalInfo?.phone || '',
      address1: profile?.billingAddress?.address1 || '',
      address2: profile?.billingAddress?.address2 || '',
      address3: profile?.billingAddress?.address3 || '',
      region: '', // Will be set by BillingAddressSection after regions load
      postalCode: profile?.billingAddress?.postalCode || '',
      country: '', // Will be set by BillingAddressSection after countries load
      billingEmail: profile?.billingAddress?.billingEmail || profile?.personalInfo?.email || '',
      // Shipping ("Site Address") — country is the shared field above; shippingRegion is
      // resolved by useShippingAddress once the region list for that country has loaded.
      shippingAddress1: profile?.shippingAddress?.address1 || '',
      shippingAddress2: profile?.shippingAddress?.address2 || '',
      shippingAddress3: profile?.shippingAddress?.address3 || '',
      shippingRegion: '',
      shippingPostalCode: profile?.shippingAddress?.postalCode || '',
      // Resolved by useShippingAddress once the profile has loaded, from the flag HaloPSA
      // actually saved last time — see ShippingAddressDto.SameAsBilling.
      shippingSameAsBilling: false,
      contactName: profile?.mainContact?.name || '',
      contactEmail: profile?.mainContact?.email || '',
      contactPhone: profile?.mainContact?.phone || '',
      mainContactId: '', // Will be set by MainContactSection after options load
      mainContactUsername: '' // Will be set by MainContactSection after selection
    }
  });

  // Owns the contact list so the resolver above can see whether a selection is even possible.
  const mainContact = useMainContact({ canEditBillingAndContact, setValue, watch });
  formContext.hasContactOptions = mainContact.contactOptions.length > 0;

  // Same rule as the Team page's Edit User drawer: Save stays disabled while a changed username is
  // being looked up, is already taken, is empty, or has characters the server rejects. The server
  // enforces all of it too, this just says so before the round trip.
  const usernameStatus = useUsernameAvailability(watch('username'), profile?.personalInfo?.username);
  const usernameBlocksSave = ['required', 'invalid', 'checking', 'taken'].includes(usernameStatus);
  // Why Save is disabled, for when the Username field is not on screen (see the actions row).
  const usernameSaveHint = {
    required: 'Username is required. Add it on My Profile to save.',
    invalid: 'Invalid characters in username. Change it on My Profile to save.',
    taken: 'Username is already taken. Change it on My Profile to save.',
  }[usernameStatus];

  /**
   * A blocked submit used to abort with nothing on screen. The field at fault can belong to a
   * section this role cannot see (that is how the country requirement locked out License Admins and
   * Registered Contacts), leaving no error text to render and a Save button that looked dead. Even
   * with that rule fixed, any future field in a hidden section would fail the same silent way, so
   * the reason is always surfaced.
   */
  const onInvalid = (formErrors) => {
    const [firstField, firstError] = Object.entries(formErrors).find(([, error]) => error?.message) || [];
    showToast.error(firstError?.message || 'Please correct the highlighted fields and try again.');
    // Save is shared by both tabs, so the field at fault may sit on the one that is hidden.
    if (firstField) {
      const errorView = PERSONAL_FIELDS.has(firstField) ? 'personal' : 'organization';
      if (errorView !== view) onViewChange?.(errorView);
    }
  };

  const onSubmit = async (data) => {
    const { getClientId, getSiteId } = useUserStore.getState();
    const clientId = getClientId();
    const siteId = getSiteId();

    try {
      // Show full page loader
      setIsLoading(true);

      const payload = buildProfilePayload({
        data,
        canEditBillingAndContact,
        getCountryById,
        getRegionById,
        siteId,
        clientId,
      });
      const result = await updateProfile(payload);

      // Store the selected main contact ID for later use
      if (canEditBillingAndContact && data.mainContactId) {
        sessionStorage.setItem('lastSelectedMainContactId', data.mainContactId);
      }

      if (!result?.success || !result?.data) {
        return;
      }

      // Update store with new data
      updateStoreProfile(result.data);

      reset(buildProfileResetData(
        result.data,
        mainContact.selectedContactId,
        watch('shippingRegion'),
        watch('mainContactUsername'),
      ));
    } catch (error) {
      // Error is already handled by the service with toast
      console.error('Profile update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6" data-testid={dataTestId || 'profile-form'}>
      {/* ── Organization Profile tab ── */}
      <div className={view === 'organization' ? 'space-y-6' : 'hidden'} data-testid="profile-organization-view">
        {/* Organization Profile */}
        <OrganizationSection
          firmName={profile?.firm?.firmName || ''}
          canEditCompany={canEditCompany}
          register={register}
          errors={errors}
          dataTestId="profile-org-section"
        />

        {/* MFA (account owner only) — shown directly under the company name. Visible when admin
            allows MFA (interactive) OR MFA is still enabled while admin revoked it (read-only note). */}
        {hasAccountOwner && (profile?.firm?.isMfaAllowed || profile?.firm?.mfaEnabled) && (
          <MfaSection onLoadingChange={onLoadingChange} dataTestId="profile-mfa-section" />
        )}

        {canViewBillingAndContact && (
          <div className="border-b border-line my-4"></div>
        )}

        {/* Billing Address */}
        {canViewBillingAndContact && (
          <BillingAddressSection
            canEditBillingAndContact={canEditBillingAndContact}
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            isSubmitted={isSubmitted}
            dataTestId="profile-billing-section"
          />
        )}
        {canViewBillingAndContact && (
          <div className="border-b border-line my-4"></div>
        )}

        {/* Shipping Address — same account-owner/billing-admin gating as Billing above */}
        {canViewBillingAndContact && (
          <ShippingAddressSection
            canEditBillingAndContact={canEditBillingAndContact}
            register={register}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            isSubmitted={isSubmitted}
            dataTestId="profile-shipping-section"
          />
        )}
        {canViewBillingAndContact && (
          <div className="border-b border-line my-4"></div>
        )}

        {/* Main Contact */}
        {canViewBillingAndContact && (
          <MainContactSection
            canEditBillingAndContact={canEditBillingAndContact}
            register={register}
            errors={errors}
            contactOptions={mainContact.contactOptions}
            loadingContacts={mainContact.loadingContacts}
            selectedContactId={mainContact.selectedContactId}
            handleContactChange={mainContact.handleContactChange}
            canLoadContacts={mainContact.canLoadContacts}
            dataTestId="profile-contact-section"
          />
        )}

        {hasAccountOwner && (
          <TransferOwnershipSection dataTestId="profile-transfer-section" />
        )}

        {/* Delete Account is deliberately not offered here for now. DeleteAccountSection and its
            service are intact — restoring this render (for Account Owners, as before) brings it back:
            {hasAccountOwner && <DeleteAccountSection dataTestId="profile-delete-section" />} */}
      </div>

      {/* ── My Profile tab ── */}
      <div className={view === 'personal' ? 'space-y-6' : 'hidden'} data-testid="profile-personal-view">
        {/* Personal Information */}
        <PersonalInfoSection
          register={register}
          errors={errors}
          usernameStatus={usernameStatus}
          dataTestId="profile-personal-section"
        />

        {/* Authenticator (all roles) — the user's own TOTP setup/reset, so it lives on My Profile.
            Separate from the firm-wide "Require MFA for all users" setting on Organization Profile.
            Not a form field: it has its own status check and actions, so Save is unaffected. */}
        <AuthenticatorSection onLoadingChange={onLoadingChange} dataTestId="profile-authenticator-section" />

        {/* Dark Mode Toggle */}
        <div className="hidden flex items-center justify-between p-4 border border-line rounded-xl" data-testid="profile-dark-mode-section">
          <div>
            <h4 className="text-sm font-medium text-fg" data-testid="profile-dark-mode-title">Dark Mode</h4>
            <p className="text-xs text-fg-muted" data-testid="profile-dark-mode-description">Use dark theme across the application</p>
          </div>
          {/* Shared Switch rather than a hand-rolled button, so it matches every other toggle in the
              app (and picks up the themed thumb automatically). */}
          <Switch
            checked={isDarkMode}
            onCheckedChange={toggleTheme}
            dataTestId="profile-dark-mode-toggle"
          />
        </div>
      </div>

      {/* Save Actions — shared by both tabs: it saves the whole form, whichever half is showing. */}
      <div className={saveRow} data-testid="profile-actions">
        {/* Save is shared by both tabs but Username lives only on My Profile — without this, an
            edit made there would leave Save disabled on Organization Profile with no visible reason. */}
        {usernameSaveHint && view !== 'personal' && (
          <p className={`${errorTextClassName} mr-auto self-center`} data-testid="profile-save-blocked-username">
            {usernameSaveHint}
          </p>
        )}
        <Button
          type="submit"
          disabled={isSubmitting || usernameBlocksSave}
          className={saveButton}
          data-testid="profile-save-button"
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
